import { ESdChangeReason, ILanguageModel, ViewPart } from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { DESIGNER_LANGUAGE_OPTIONS } from "../../../../shared/utility/designer-languages";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import {
  MCP_BACKEND_NOTIFICATION_EVENT,
  consumePendingNotifications,
  type BackendNotificationPayload
} from "../../../../shared/utility/mcp-backend-notifications";
import { DeleteModal } from "../../../common/delete-modal";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCheckbox from "../../../ui-kit/kuika-checkbox/kuika-checkbox";
import KuikaDropdownMenu, { KuikaDropdownMenuItemType } from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaEmptyState from "../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaMenuItem from "../../../ui-kit/kuika-menu-item/kuika-menu-item";
import KuikaSpinnerSmall from "../../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaText from "../../../ui-kit/typography/Text/Text";
import "./language-panel.scss";
import SearchablePanelHeader from "../searchable-panel-header/searchable-panel-header";
import { LanguageTranslateModal } from "./language-translate-modal";

export interface ILanguageWindowState {
  isSearchActive: boolean;
  isModalActive: boolean;
  selectedLanguage: ILanguageModel;
  triggerComponentDeleteModal: boolean;
  searchText: string;
  selectedLanguages: Set<string>;
  isNewLanguageMenuOpen: boolean;
  isAddingLanguage: boolean;
  mcpHighlightAll: boolean;
  mcpHighlightIsDelete: boolean;
}

export interface ILanguageWindowProps {}

class LanguageWindow extends PureSdComponent<ILanguageWindowProps & IWithSDProps, ILanguageWindowState> {
  // The catalog is shared with the Email Builder's language control so the two can
  // never drift; see shared/utility/designer-languages.
  languageOptions = DESIGNER_LANGUAGE_OPTIONS;

  constructor(props: ILanguageWindowProps & IWithSDProps) {
    super(props);
    this.state = {
      isSearchActive: false,
      isModalActive: false,
      selectedLanguage: {} as ILanguageModel,
      triggerComponentDeleteModal: false,
      searchText: "",
      selectedLanguages: new Set(),
      isNewLanguageMenuOpen: false,
      isAddingLanguage: false,
      mcpHighlightAll: false,
      mcpHighlightIsDelete: false
    };
  }

  private mcpHighlightTimer: ReturnType<typeof setTimeout> | null = null;

  async componentDidMount() {
    this.handleSearchTextOnInit();

    // MCP backend notification listener
    window.addEventListener(MCP_BACKEND_NOTIFICATION_EVENT, this.handleMcpNotification as EventListener);

    // Check for pending notifications
    const pending = consumePendingNotifications("panel-language");
    if (pending.length > 0) {
      const latest = pending[pending.length - 1];
      this.applyMcpHighlight(latest);
    }
  }

  componentWillUnmount() {
    window.removeEventListener(MCP_BACKEND_NOTIFICATION_EVENT, this.handleMcpNotification as EventListener);
    if (this.mcpHighlightTimer) clearTimeout(this.mcpHighlightTimer);
  }

  handleMcpNotification = (event: CustomEvent<BackendNotificationPayload>) => {
    const payload = event.detail;
    if (payload.target !== "panel-language") return;
    this.applyMcpHighlight(payload);
  };

  applyMcpHighlight = (payload: BackendNotificationPayload) => {
    if (this.mcpHighlightTimer) clearTimeout(this.mcpHighlightTimer);

    this.setState({
      mcpHighlightAll: true,
      mcpHighlightIsDelete: payload.isDelete
    });

    this.mcpHighlightTimer = setTimeout(() => {
      this.setState({ mcpHighlightAll: false, mcpHighlightIsDelete: false });
      this.mcpHighlightTimer = null;
    }, 4000);
  };

  handleSearchTextOnInit = () => {
    const searchText = this.props.sd.searchTexts[ViewPart.LanguagePanel];
    if (searchText && searchText.length > 0) {
      this.setState({ isSearchActive: true, searchText });
    }
  };

  closeLanguageTranslateModal = () => {
    this.setState({ isModalActive: false }, () => {
      this.props.sd.loadScreen(this.props.sd.sdId, true, true);
    });
  };

  openLanguageModal = async () => {
    this.props.sd.processWithSpinner(async () => {
      await this.props.sd.saveScreen(true);
      this.setState({ isModalActive: true });
    });
  };

  handleSearchFocus = (event) => event.target.select();

  deleteSearchText = (e: any) => {
    e.stopPropagation();
    this.props.sd.setSearchTexts(ViewPart.LanguagePanel, "");
    this.setState({ searchText: "", isSearchActive: false });
  };

  handleSearchInputChange = (searchText: string) => {
    this.setState({
      searchText
    });
    this.props.sd.setSearchTexts(ViewPart.LanguagePanel, searchText);
  };

  async deleteLanguage() {
    const project = _.cloneDeep(this.props.sd.project);
    let languageData = this.props.sd.getScreenDesign().languages
      ? _.cloneDeep(this.props.sd.getScreenDesign().languages)
      : [];
    languageData = languageData.filter((x) => x.id !== this.state.selectedLanguage.id);
    const { languages } = project;
    languages.forEach((element, idx) => {
      if (this.state.selectedLanguage.id === element.id) {
        languages.splice(idx, 1);
      }
    });

    project.languages = languages;

    project.projectTranslations =
      project.projectTranslations?.filter((x) => x.language !== this.state.selectedLanguage.name) || [];

    this.props.sd.updateLanguageData(languageData);
    await this.props.sd.updateProject(project);
  }

  languageMenu = (language: ILanguageModel): KuikaDropdownMenuItemType[] => {
    let menu = [
      {
        label: "Translate",
        icon: <KuikaSvg width={16} height={16} name="language" type="sidebar-icons" />,
        onClick: () => {
          this.setState({ selectedLanguage: language }, () => {
            this.openLanguageModal();
          });
        }
      },
      {
        label: "Set as Default",
        icon: <KuikaSvg width={16} height={16} name="star" />,
        onClick: async () => {
          const project = _.cloneDeep(this.props.sd.project);
          project.languages.forEach((x) => (x.isDefault = x.id === language.id));
          await this.props.sd.updateProject(project);
        }
      },
      {
        label: "Delete",
        icon: <KuikaSvg width={16} height={16} name="trash" />,
        danger: true,
        onClick: () => {
          this.setState({
            selectedLanguage: language,
            triggerComponentDeleteModal: true
          });
        }
      }
    ];

    if (language.isDefault) {
      menu = menu.filter((item) => item.label !== "Set as Default");
    }

    return menu;
  };

  handleAddNewLanguage = async () => {
    try {
      if (this.state.selectedLanguages.size === 0) {
        KMainFunctions.displayWarningNotification("Please select at least one language.");
        return;
      }

      this.setState({ isAddingLanguage: true });
      const project = _.cloneDeep(this.props.sd.project);
      const selectedLanguages = this.languageOptions.filter((lang) => this.state.selectedLanguages.has(lang.value));

      if (!project.languages) {
        project.languages = selectedLanguages.map((lang, index) => ({
          id: Guid.create().toString(),
          name: lang.value,
          shortName: lang.label,
          isDefault: index === 0
        }));
      } else {
        project.languages = project.languages.filter((lang) => {
          return lang.isDefault || this.state.selectedLanguages.has(lang.name);
        });

        for (const lang of selectedLanguages) {
          if (project.languages.find((x) => x.name === lang.value)) {
            continue;
          }
          project.languages.push({
            id: Guid.create().toString(),
            name: lang.value,
            shortName: lang.label,
            isDefault: false
          });
        }
      }

      await this.props.sd.updateProject(project);
      this.setState({ selectedLanguages: new Set(), isAddingLanguage: false });
    } catch (error) {
      this.setState({ isAddingLanguage: false });
      KMainFunctions.exceptionHandler(error);
    }
  };

  newLanguageMenu = (): KuikaDropdownMenuItemType[] => {
    const existingLanguages = new Set(this.props.sd.project?.languages?.map((l) => l.name) || []);

    if (this.state.selectedLanguages.size === 0) {
      this.setState({ selectedLanguages: existingLanguages });
    }

    const menuItems = this.languageOptions
      .sort((a, b) => {
        if (a.value === "en_US") return -1;
        if (b.value === "en_US") return 1;
        if (a.value === "tr_TR") return -1;
        if (b.value === "tr_TR") return 1;
        if (a.value === "fr_FR") return -1;
        if (b.value === "fr_FR") return 1;
        return a.label.localeCompare(b.label);
      })
      .map((lang) => {
        const isDisabled = this.props.sd.project?.languages?.find((x) => x.name === lang.value)?.isDefault;
        return {
          label: lang.label,
          icon: <img src={`/images/flags/${lang.icon.toLowerCase()}.svg`} alt={`${lang.label} flag`} />,
          rightIcon: (
            <KuikaCheckbox
              defaultChecked={existingLanguages.has(lang.value)}
              checked={this.state.selectedLanguages.has(lang.value)}
              disabled={isDisabled}
              onChange={(e) => {
                const newSelected = new Set(this.state.selectedLanguages);
                if (e.target.checked) {
                  newSelected.add(lang.value);
                } else {
                  newSelected.delete(lang.value);
                }
                this.setState({ selectedLanguages: newSelected });
                e.stopPropagation();
              }}
            />
          ),
          key: lang.value,
          onClick: () => {
            if (isDisabled) return;
            const newSelected = new Set(this.state.selectedLanguages);
            if (newSelected.has(lang.value)) {
              newSelected.delete(lang.value);
            } else {
              newSelected.add(lang.value);
            }
            this.setState({ selectedLanguages: newSelected });
          },
          showSelectedIcon: false
        };
      });

    return [...menuItems];
  };

  render() {
    const filteredLanguages = this.props.sd.project?.languages?.filter(
      (x) =>
        x.shortName.toLowerCase().indexOf(KMainFunctions.turkishCharsToEnglish(this.state.searchText).toLowerCase()) !==
        -1
    );

    const hasLanguages = this.props.sd.project?.languages && this.props.sd.project.languages.length > 0;
    const hasSearchResults = filteredLanguages && filteredLanguages.length > 0;

    return (
      <>
        {this.state.isModalActive && (
          <LanguageTranslateModal
            sd={this.props.sd}
            showModal={this.state.isModalActive}
            closeModal={this.closeLanguageTranslateModal}
            selectedLanguage={this.state.selectedLanguage}
            languageOptions={this.languageOptions}
          />
        )}
        <DeleteModal
          itemName={this.state.selectedLanguage.shortName}
          triggerModal={this.state.triggerComponentDeleteModal}
          deleteItem={() => this.deleteLanguage()}
          deleteType={"Language"}
          closeModal={() => this.setState({ triggerComponentDeleteModal: false })}
        />
        <div className="kuika-left-panel__container">
          <SearchablePanelHeader
            searchText={this.state.searchText}
            onSearchTextChange={this.handleSearchInputChange}
            onSearchClearCallback={this.deleteSearchText}
            viewPart={ViewPart.LanguagePanel}
            panelName="Languages"
            isSearchActive={this.state.isSearchActive}
            setSearchActive={(value: boolean) => this.setState({ isSearchActive: value })}
            extraRightUnit={
              <KuikaDropdownMenu
                options={this.newLanguageMenu()}
                header="Add New Language"
                open={this.state.isNewLanguageMenuOpen}
                setOpen={(visible) => {
                  this.setState({ isNewLanguageMenuOpen: visible });
                  const existingLanguages = new Set(this.props.sd.project?.languages?.map((l) => l.name) || []);
                  if (visible) {
                    this.setState({ selectedLanguages: existingLanguages });
                  } else if (this.state.selectedLanguages.size !== existingLanguages.size) {
                    this.handleAddNewLanguage();
                  }
                }}
                overlayClassName="kuika-language-menu"
                menuTestId="addLanguageMenu"
                closeOnSelect={false}
              >
                <KuikaButton
                  data-testid="addLanguageButton"
                  isIcon
                  type="tertiary"
                  color="neutral"
                  active={this.state.isNewLanguageMenuOpen}
                >
                  <KuikaSvg width={16} height={16} name="plus" />
                </KuikaButton>
              </KuikaDropdownMenu>
            }
          />
          <KuikaSpinnerSmall spinning={this.state.isAddingLanguage} size="small">
            <div className="kuika-left-panel__content">
              {!hasLanguages ? (
                <KuikaEmptyState svgName="language" name="Language" description="No languages have been added yet." />
              ) : this.state.searchText && this.state.searchText.length > 0 && !hasSearchResults ? (
                <KuikaEmptyState
                  svgName="search-off"
                  customName="No Language found"
                  description="We couldn't find any items matching your search."
                />
              ) : (
                filteredLanguages?.map((ld) => (
                  <KuikaMenuItem
                    key={ld.id}
                    data-testid={`languageRow-${ld.shortName}`}
                    className={
                      this.state.mcpHighlightAll
                        ? this.state.mcpHighlightIsDelete
                          ? "kuika-menu-item--mcp-highlight-delete"
                          : "kuika-menu-item--mcp-highlight"
                        : undefined
                    }
                    title={
                      <div className="kuika-flex--gap">
                        <KuikaText>{ld.shortName}</KuikaText>
                        {ld.isDefault && <KuikaBadge content="Default"></KuikaBadge>}
                      </div>
                    }
                    rightDropdown={
                      <KuikaDropdownMenu options={this.languageMenu(ld)} overlayClassName="kuika-screens__dropdown">
                        {(open) => (
                          <KuikaButton
                            data-testid={`languageRowMenuButton-${ld.shortName}`}
                            onClick={() => this.setState({ selectedLanguage: ld })}
                            isIcon
                            type="tertiary"
                            color="neutral"
                            active={open}
                          >
                            <KuikaSvg width={16} height={16} name="dots" />
                          </KuikaButton>
                        )}
                      </KuikaDropdownMenu>
                    }
                  />
                ))
              )}
            </div>
          </KuikaSpinnerSmall>
        </div>
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const languageWindow = withSD<ILanguageWindowProps>(LanguageWindow);
export { languageWindow as LanguageWindow };
